import type { MappedRoom, MappedRoomListItem, SourceRoom, SourceRoomListItem } from './types';

const MIN_ROOMS_FOR_GROUPING = 2;

/**
 * Gets room name from various possible sources
 */
const getRoomName = (room: SourceRoom | undefined): string | null => {
  if (!room) return null;

  const name = room.name;

  return name?.trim() || null;
};

/**
 * Creates grouping key from supplier and room name
 */
const getGroupKey = (supplier: App.Bedbank.Suppliers, roomName: string): string => {
  return `${supplier}::${roomName.toLowerCase()}`;
};

/**
 * Generic grouping function - groups rooms by supplier + name
 */
function groupRooms<T extends { supplier: App.Bedbank.Suppliers }>(
  rooms: Array<T>,
  getRoomFromItem: (item: T) => SourceRoom | undefined,
  createGroup: (supplier: App.Bedbank.Suppliers, name: string, rooms: Array<T>) => any,
  ungroupedKeys?: Set<string>,
): Array<T | any> {
  // Build groups map
  const groupsMap = new Map<string, { supplier: App.Bedbank.Suppliers; name: string; rooms: Array<T> }>();

  rooms.forEach((item) => {
    const room = getRoomFromItem(item);
    const roomName = getRoomName(room);
    if (!roomName) return;

    const key = getGroupKey(item.supplier, roomName);

    if (!groupsMap.has(key)) {
      groupsMap.set(key, { supplier: item.supplier, name: roomName, rooms: [] });
    }
    groupsMap.get(key)!.rooms.push(item);
  });

  // Build result with groups or individual rooms
  const result: Array<T | any> = [];
  const processedKeys = new Set<string>();

  rooms.forEach((item) => {
    const room = getRoomFromItem(item);
    const roomName = getRoomName(room);

    if (!roomName) {
      result.push(item);
      return;
    }

    const key = getGroupKey(item.supplier, roomName);
    const group = groupsMap.get(key);

    // Don't group if this key has been marked as ungrouped
    if (ungroupedKeys?.has(key) || !group || group.rooms.length < MIN_ROOMS_FOR_GROUPING) {
      result.push(item);
      return;
    }

    if (processedKeys.has(key)) return;

    processedKeys.add(key);
    result.push(createGroup(group.supplier, group.name, group.rooms));
  });

  return result;
}

/**
 * Export the getGroupKey function for use in other components
 */
export { getGroupKey };

export const groupSourceRoomsBySupplierName = (
  rooms: Array<SourceRoom>,
  ungroupedKeys?: Set<string>,
): Array<SourceRoomListItem> => {
  return groupRooms(
    rooms,
    (room) => room,
    (supplier, name, rooms) => ({ type: 'group' as const, supplier, supplierRoomName: name, rooms }),
    ungroupedKeys,
  );
};

export const groupMappedRoomsBySupplierName = (
  rooms: Array<MappedRoom>,
  ungroupedKeys?: Set<string>,
): Array<MappedRoomListItem> => {
  return groupRooms(
    rooms,
    (room) => room.sourceContent,
    (supplier, name, rooms) => ({ type: 'group' as const, supplier, supplierRoomName: name, rooms }),
    ungroupedKeys,
  );
};
